// MobileNav — hamburger-trigger + slide-in drawer для viewport <720px.
// Закрывает gap из D09 §1: на mobile sidebar (.client-side) скрыт через
// media-query, поэтому без drawer-а клиент после первой загрузки заперт
// (нет ни ссылок на /catalog, /orders, /profile, ни кнопки logout).
//
// A11y (D09 §7): aria-label/aria-expanded на trigger, role="dialog"+aria-modal
// на drawer, ESC закрывает, focus-trap внутри drawer, фокус возвращается на
// trigger при закрытии, фон страницы помечается aria-hidden пока drawer открыт.
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Menu, X } from 'lucide-react'
import { useLocation } from '../lib/router'
import { ClientBrandBlock } from './ClientBrandBlock'
import { ClientNavLinks, type ClientNavActive } from './ClientNavLinks'

export interface MobileNavProps {
  active: ClientNavActive
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])'

export function MobileNav({ active }: MobileNavProps) {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const drawerRef = useRef<HTMLDivElement>(null)
  const closeBtnRef = useRef<HTMLButtonElement>(null)
  const { pathname } = useLocation()

  // Закрываем drawer при смене pathname. Кейс: пользователь кликнул ссылку
  // внутри drawer-а — onNavigate уже вызвал setOpen(false), но если переход
  // случился без явного клика (например, программный navigate из другого
  // компонента), мы тоже хотим скрыться.
  const lastPathRef = useRef(pathname)
  useEffect(() => {
    if (lastPathRef.current !== pathname) {
      lastPathRef.current = pathname
      setOpen(false)
    }
  }, [pathname])

  // Эффект-A: на открытие — авто-фокус на close-кнопку, body scroll lock,
  // aria-hidden на остальной странице (чтобы скрин-ридер не «видел» фон под
  // drawer). На закрытие — возвращаем фокус на trigger, чтобы пользователь
  // клавиатуры не «потерялся» в конце документа.
  //
  // Зависим ТОЛЬКО от `open` — иначе любое внешнее изменение пропсов рестартит
  // эффект и сбивает фокус (см. урок Modal.tsx Эффект-A).
  useLayoutEffect(() => {
    if (!open) return
    // Захватываем trigger-DOM-node в момент открытия. react-hooks/exhaustive-deps
    // справедливо ругается на triggerRef.current в cleanup — к моменту cleanup
    // ref-узел теоретически может смениться; копирование в локальную переменную
    // фиксирует node, на который нужно вернуть фокус.
    const triggerEl = triggerRef.current
    closeBtnRef.current?.focus()
    const prevBodyOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const shell = document.querySelector('.client-shell')
    const prevAriaHidden = shell?.getAttribute('aria-hidden')
    shell?.setAttribute('aria-hidden', 'true')
    return () => {
      document.body.style.overflow = prevBodyOverflow
      if (prevAriaHidden === null || prevAriaHidden === undefined) {
        shell?.removeAttribute('aria-hidden')
      } else {
        shell?.setAttribute('aria-hidden', prevAriaHidden)
      }
      triggerEl?.focus()
    }
  }, [open])

  // Эффект-Б: keydown — ESC закрывает, Tab/Shift+Tab делают focus-trap внутри
  // drawer-а. Без trap фокус уплывает на скрытые элементы фона и пользователь
  // клавиатуры теряет ориентацию.
  useEffect(() => {
    if (!open) return
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault()
        setOpen(false)
        return
      }
      if (event.key !== 'Tab' || !drawerRef.current) return
      const focusables = Array.from(
        drawerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((el) => !el.hasAttribute('aria-hidden'))
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      if (!first || !last) return
      const current = document.activeElement as HTMLElement | null
      if (event.shiftKey) {
        if (current === first || !drawerRef.current.contains(current)) {
          event.preventDefault()
          last.focus()
        }
      } else {
        if (current === last || !drawerRef.current.contains(current)) {
          event.preventDefault()
          first.focus()
        }
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  const trigger = (
    <button
      ref={triggerRef}
      type="button"
      className="client-mobile-nav-trigger"
      aria-label="Открыть меню"
      aria-expanded={open}
      aria-controls="client-mobile-drawer"
      data-testid="client-mobile-nav-trigger"
      onClick={() => setOpen(true)}
    >
      <Menu size={20} aria-hidden="true" />
    </button>
  )

  if (!open || typeof document === 'undefined') {
    return trigger
  }

  const drawer = (
    // role="presentation" сообщает a11y-tree: overlay — это техническая
    // backdrop-обёртка для click-outside-to-close, не интерактивный элемент.
    // Сценарий keyboard-only при этом работает через ESC (см. Эффект-Б).
    <div
      role="presentation"
      className="client-mobile-overlay"
      data-testid="client-mobile-overlay"
      onClick={() => setOpen(false)}
    >
      {/* jsx-a11y: stopPropagation на dialog нужен, чтобы клик внутри drawer-а
          не всплывал на overlay (где висит close-on-click). Это технический
          guard, а не interactive-handler — отсюда disable rule-ов. */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div
        ref={drawerRef}
        id="client-mobile-drawer"
        className="client-mobile-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Главное меню"
        data-testid="client-mobile-drawer"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="client-mobile-drawer__header">
          <div style={{ flex: 1, minWidth: 0 }}>
            <ClientBrandBlock />
          </div>
          <button
            ref={closeBtnRef}
            type="button"
            className="client-mobile-drawer__close"
            aria-label="Закрыть меню"
            data-testid="client-mobile-drawer-close"
            onClick={() => setOpen(false)}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>
        <nav className="client-mobile-drawer__nav" aria-label="Главное меню">
          <ClientNavLinks active={active} onNavigate={() => setOpen(false)} />
        </nav>
      </div>
    </div>
  )

  return (
    <>
      {trigger}
      {createPortal(drawer, document.body)}
    </>
  )
}
